import { useCallback, useState } from 'react';
import type {
  IFilterContext,
} from '@/creditflowresearch/application/BondScreener/createUseMultiSelectFilter';
import { hasValue } from '@/creditflowresearch/application/BondScreener/filterValueHelpers';

type TExcludableKeys<T> = {
  [K in keyof T]: T[K] extends string[] | number | null | undefined ? K : never;
}[keyof T];

type TBooleanKeys<T> = {
  [K in keyof T]: T[K] extends boolean | undefined ? K : never;
}[keyof T];

type TKeyOrKeys<T, K extends TExcludableKeys<T>> = K | K[];

interface IUseExcludeFiltersProps<T, K extends TExcludableKeys<T>> {
  includeKey: TKeyOrKeys<T, K>,
  excludeKey: TKeyOrKeys<T, K>,
  stateKey: TBooleanKeys<T>,
  useFilterContext: () => IFilterContext<T>,
}

interface IUseExcludeFiltersReturn<K> {
  isExcluded: boolean,
  activeKey: K,
  onToggleExclude: () => void,
}

const getEmptyValue = (value: unknown): unknown => (Array.isArray(value) ? [] : null);

const toArray = <T>(value: T | T[]): T[] => (Array.isArray(value) ? value : [value]);

const useExcludeToggle = <T extends Record<string, unknown>, K extends TExcludableKeys<T>>({
  includeKey,
  excludeKey,
  stateKey,
  useFilterContext,
}: IUseExcludeFiltersProps<T, K>): IUseExcludeFiltersReturn<K> => {
  const { localFilters, setLocalFilters } = useFilterContext();
  const [isExcluded, setIsExcluded] = useState(() => (
    toArray(excludeKey).some((key) => hasValue(localFilters[key]))
  ));

  const onToggleExclude = useCallback(() => {
    setIsExcluded((wasExcluded) => {
      const includeKeys = toArray(includeKey);
      const excludeKeys = toArray(excludeKey);
      setLocalFilters((prev) => {
        const updates: Record<string, unknown> = { [stateKey]: !wasExcluded };
        for (let i = 0; i < includeKeys.length; i++) {
          const currentKey = wasExcluded ? excludeKeys[i] : includeKeys[i];
          const newKey = wasExcluded ? includeKeys[i] : excludeKeys[i];
          updates[newKey as string] = prev[currentKey];
          updates[currentKey as string] = getEmptyValue(prev[currentKey]);
        }
        return { ...prev, ...updates };
      });
      return !wasExcluded;
    });
  }, [includeKey, excludeKey, stateKey, setLocalFilters]);

  const includeKeys = toArray(includeKey);
  const excludeKeys = toArray(excludeKey);

  return {
    isExcluded,
    activeKey: (isExcluded ? excludeKeys[0] : includeKeys[0]) as K,
    onToggleExclude,
  };
};

export default useExcludeToggle;
